/**
 * @fileoverview Audit logging service for HIPAA compliance
 * @module services/audit
 * @description
 * Provides audit trail functionality for tracking all data operations.
 * Ported 1:1 from the Angular AuditLogService; the injected `DataService` is
 * wired via a direct import of the ported `dataService` singleton. Exposed as a
 * singleton (`auditLogService`) preserving the exact public method names.
 *
 * @author Liraz Amir
 * @since MVP-26
 */

import { dataService } from '@/services/data/data.service';

/**
 * Audit log action types
 */
export type AuditActionType =
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE'
  | 'LOGIN'
  | 'LOGOUT'
  | 'AUTH_FAILED'
  | 'FILE_ACCESS';

/**
 * Audit log entry interface
 */
export interface AuditLogEntry {
  id: string;
  user_id: string;
  user_email: string;
  action_type: AuditActionType;
  table_name: string;
  record_id?: string;
  ip_address?: string;
  user_agent?: string;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  access_reason?: string;
  created_at: string;
}

/**
 * Options for logging audit events
 */
export interface AuditLogOptions {
  recordId?: string;
  changes?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  accessReason?: string;
}

class AuditLogService {
  private data = dataService;

  /**
   * Log a CREATE operation
   */
  async logCreate(
    tableName: string,
    recordId: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await this.logEvent('CREATE', tableName, { recordId, metadata });
  }

  /**
   * Log a READ operation
   */
  async logRead(
    tableName: string,
    recordId: string,
    accessReason?: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await this.logEvent('READ', tableName, { recordId, accessReason, metadata });
  }

  /**
   * Log an UPDATE operation
   */
  async logUpdate(
    tableName: string,
    recordId: string,
    changes: Record<string, unknown>,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await this.logEvent('UPDATE', tableName, { recordId, changes, metadata });
  }

  /**
   * Log a DELETE operation
   */
  async logDelete(
    tableName: string,
    recordId: string,
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await this.logEvent('DELETE', tableName, { recordId, metadata });
  }

  /**
   * Log a successful LOGIN event
   */
  async logLogin(metadata?: Record<string, unknown>): Promise<void> {
    await this.logEvent('LOGIN', 'auth', { metadata });
  }

  /**
   * Log a LOGOUT event
   */
  async logLogout(metadata?: Record<string, unknown>): Promise<void> {
    await this.logEvent('LOGOUT', 'auth', { metadata });
  }

  /**
   * Log a failed authentication attempt
   */
  async logAuthFailed(reason: string, metadata?: Record<string, unknown>): Promise<void> {
    await this.logEvent('AUTH_FAILED', 'auth', {
      metadata: { ...metadata, reason },
    });
  }

  /**
   * Log a file access event
   */
  async logFileAccess(
    bucket: string,
    filePath: string,
    operation: 'upload' | 'download' | 'delete',
    metadata?: Record<string, unknown>,
  ): Promise<void> {
    await this.logEvent('FILE_ACCESS', 'storage', {
      recordId: `${bucket}/${filePath}`,
      metadata: { ...metadata, bucket, filePath, operation },
    });
  }

  /**
   * Get recent audit logs for current user (last 30 days)
   */
  async getRecentLogs(limit: number = 50): Promise<AuditLogEntry[]> {
    try {
      const { data, error } = await this.data.client
        .from('recent_audit_logs')
        .select('*')
        .limit(limit);

      if (error) {
        console.error('Failed to fetch recent audit logs:', error);
        return [];
      }

      return (data as AuditLogEntry[]) || [];
    } catch (error) {
      console.error('Error fetching recent audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs for a specific table and record
   */
  async getLogsForRecord(
    tableName: string,
    recordId: string,
    limit: number = 20,
  ): Promise<AuditLogEntry[]> {
    try {
      const { data, error } = await this.data.client
        .from('audit_logs')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Failed to fetch record audit logs:', error);
        return [];
      }

      return (data as AuditLogEntry[]) || [];
    } catch (error) {
      console.error('Error fetching record audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit log statistics (admin only)
   */
  async getAuditStats(): Promise<unknown[] | null> {
    try {
      const { data, error } = await this.data.client
        .from('audit_log_stats')
        .select('*')
        .limit(30);

      if (error) {
        console.error('Failed to fetch audit stats:', error);
        return null;
      }

      return data as unknown[];
    } catch (error) {
      console.error('Error fetching audit stats:', error);
      return null;
    }
  }

  /**
   * Core method to log an audit event
   */
  private async logEvent(
    actionType: AuditActionType,
    tableName: string,
    options: AuditLogOptions = {},
  ): Promise<void> {
    try {
      // Get current user
      const {
        data: { user },
      } = await this.data.client.auth.getUser();

      if (!user) {
        console.warn('Cannot log audit event: no authenticated user');
        return;
      }

      // Get user agent from browser
      const userAgent = navigator.userAgent;

      // Note: IP address is not available from browser for security reasons
      // It will be captured server-side if needed

      // Call the database function to log the event
      const { error } = await this.data.client.rpc('log_audit_event', {
        p_user_id: user.id,
        p_user_email: user.email || 'unknown',
        p_action_type: actionType,
        p_table_name: tableName,
        p_record_id: options.recordId || null,
        p_ip_address: null, // Server-side only
        p_user_agent: userAgent,
        p_changes: options.changes || null,
        p_metadata: options.metadata || {},
        p_access_reason: options.accessReason || null,
      });

      if (error) {
        console.error('Failed to log audit event:', error);
        // Don't throw - audit logging should not break application flow
      }
    } catch (error) {
      console.error('Error logging audit event:', error);
      // Don't throw - audit logging should not break application flow
    }
  }
}

export const auditLogService = new AuditLogService();
export type { AuditLogService };
